import ConditionalWrapper from "@splitfire-agency/raiden-library/dist/components/ConditionalWrapper";
import encodeFormData from "@splitfire-agency/raiden-library/dist/libraries/utils/encodeFormData";
import { useRouter } from "next/router";
import { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import {
  useApiError,
  withApiError,
} from "../../../components/ReactHookForm/ApiError";
import { HOLDERS_TYPES_VALUE_CHILD } from "../../../constants/holders";
import { getPreferredYear } from "../../../helpers/years/getPreferredYear";
import { useConfiguration } from "../../../hooks/useConfiguration";
import useRequest from "../../../hooks/useRequest";
import generateApiUri from "../../../libraries/utils/generateApiUri";

export const HOLDERS_CREATE_FORM_ID = "holders-create-form";

/**
 * @typedef {object} Props
 * @property {number} environmentId
 * @property {boolean} [withForm]
 * @property {string} [initialHolderState]
 * @property {(params: {holder: import("../../../types/Holder").Holder}) => void} onSuccess
 * @property {(params: {submit: () => void, isLoading: boolean}) => import("react").ReactElement} render
 *
 * @param {Props} props
 */
function HoldersCreateHandler({
  environmentId,
  withForm = true,
  initialHolderState = "",
  onSuccess,
  render,
}) {
  const intl = useIntl();

  const { resolveApiError, resetApiErrors } = useApiError();

  const router = useRouter();

  const { configuration } = useConfiguration();

  const form = useForm({
    defaultValues: {
      data: {
        type: "",
        environment_id: String(environmentId ?? ""),
        from: "",
        to: "",
        customer_id: router.query.customerId ?? "",
        pai: false,
        comment: "",
        state: initialHolderState,
        schoolings: [
          {
            year_id:
              getPreferredYear({
                configuration,
                environmentId: String(environmentId ?? ""),
              })?.id ?? "",
            school_id: "",
            classroom_id: "",
            level: "",
          },
        ],
      },
    },
  });
  const { handleSubmit, reset } = form;

  const { request, isLoading, toastSuccess } = useRequest();

  const submit = useMemo(() => {
    return handleSubmit((_values) => {
      const values = {
        data: {
          ..._values.data,
          state: _values.data.state !== "" ? _values.data.state : undefined,
          schoolings:
            _values.data.type === HOLDERS_TYPES_VALUE_CHILD
              ? _values.data.schoolings
              : undefined,
        },
      };
      request(
        generateApiUri({
          id: "@holders.create",
          query: { fields: ["holder.id_card", "holder.schoolings"] },
        }),
        {
          method: "POST",
          body: encodeFormData(values),
        },
        {
          inputMode: "formData",
        },
      )
        .then((response) => {
          reset();
          resetApiErrors();
          toastSuccess({
            title: intl.formatMessage({
              defaultMessage: "L'adhérent a bien été créé.",
            }),
          });
          onSuccess?.({ holder: response.data });
        })
        .catch((response) => {
          resolveApiError({ response, form });
        });
    });
  }, [
    form,
    handleSubmit,
    intl,
    onSuccess,
    request,
    reset,
    resetApiErrors,
    resolveApiError,
    toastSuccess,
  ]);

  return (
    <FormProvider {...form}>
      <ConditionalWrapper
        hasWrapper={withForm}
        wrapper={(children) => (
          <form id={HOLDERS_CREATE_FORM_ID} onSubmit={submit}>
            {children}
          </form>
        )}>
        {render({
          submit,
          isLoading,
        })}
      </ConditionalWrapper>
    </FormProvider>
  );
}
export default withApiError(HoldersCreateHandler);
